import { useId, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Building2, Car, Package, Pencil, Plus, Trash2, Wrench } from "lucide-react";
import {
  CA_POSTAL_RE,
  CreateRooftopSchema,
  PhoneE164Schema,
  RooftopStatusSchema,
  StateOrProvinceCodeSchema,
  US_ZIP_RE,
  ZipOrPostalCodeSchema,
  stateOptionsForCountry,
  timezonesForCountry,
  type Rooftop,
  type UpdateRooftopDto,
} from "@lumika/shared";
import { z } from "zod";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  PageError,
  PageLoading,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";

// Form-local schemas accept "" (empty input) AND a strictly-shaped
// non-empty string. `toPayload` converts "" → null before hitting
// the server DTO, so the SERVER schema is strict (E.164 / ZIP+4 /
// 2-letter state) and the FORM schema validates the same shape
// before submit so the user sees an inline error instead of a
// server-side 400.
const RooftopFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(200),
  status: RooftopStatusSchema,
  websiteUrl: z
    .union([z.literal(""), z.string().url("Use a full URL, e.g. https://example.com.")])
    .optional(),
  primaryEmail: z
    .union([z.literal(""), z.string().email("Invalid email.")])
    .optional(),
  primaryPhone: z.union([z.literal(""), PhoneE164Schema]).optional(),
  state: z.union([z.literal(""), StateOrProvinceCodeSchema]).optional(),
  city: z.string().trim().max(80).optional(),
  zip: z.union([z.literal(""), ZipOrPostalCodeSchema]).optional(),
  addressLine1: z.string().trim().max(160).optional(),
  timezone: z.string().trim().min(1).max(64),
  salesNotApplicable: z.boolean(),
  serviceNotApplicable: z.boolean(),
  partsNotApplicable: z.boolean(),
});

type RooftopFormValues = z.infer<typeof RooftopFormSchema>;

// The base fields accept either country's shape (the server stores the
// union); the dealership's country narrows the zip format here. The
// state field needs no extra rule — its dropdown only offers the
// country's own codes.
function rooftopFormSchemaForCountry(country: string) {
  const isCa = country === "CA";
  return RooftopFormSchema.superRefine((values, ctx) => {
    if (values.zip && !(isCa ? CA_POSTAL_RE : US_ZIP_RE).test(values.zip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["zip"],
        message: isCa
          ? "Use a Canadian postal code, e.g. A1A 1A1."
          : "Use ZIP (12345) or ZIP+4 (12345-6789).",
      });
    }
  });
}

const EMPTY: RooftopFormValues = {
  name: "",
  status: "active",
  websiteUrl: "",
  primaryEmail: "",
  primaryPhone: "",
  state: "",
  city: "",
  zip: "",
  addressLine1: "",
  timezone: "America/New_York",
  salesNotApplicable: false,
  serviceNotApplicable: false,
  partsNotApplicable: false,
};

function toEditValues(rooftop: Rooftop): RooftopFormValues {
  return {
    name: rooftop.name,
    status: rooftop.status,
    websiteUrl: rooftop.websiteUrl ?? "",
    primaryEmail: rooftop.primaryEmail ?? "",
    primaryPhone: rooftop.primaryPhone ?? "",
    state: rooftop.state ?? "",
    city: rooftop.city ?? "",
    zip: rooftop.zip ?? "",
    addressLine1: rooftop.addressLine1 ?? "",
    timezone: rooftop.timezone,
    salesNotApplicable: rooftop.salesNotApplicable ?? false,
    serviceNotApplicable: rooftop.serviceNotApplicable ?? false,
    partsNotApplicable: rooftop.partsNotApplicable ?? false,
  };
}

function toPayload(values: RooftopFormValues): UpdateRooftopDto {
  return CreateRooftopSchema.omit({ dealershipId: true })
    .partial()
    .parse({
      name: values.name.trim(),
      status: values.status,
      websiteUrl: values.websiteUrl?.trim() ? values.websiteUrl.trim() : null,
      primaryEmail: values.primaryEmail?.trim() ? values.primaryEmail.trim() : null,
      primaryPhone: values.primaryPhone?.trim() ? values.primaryPhone.trim() : null,
      state: values.state?.trim() ? values.state.trim() : null,
      city: values.city?.trim() ? values.city.trim() : null,
      zip: values.zip?.trim() ? values.zip.trim() : null,
      addressLine1: values.addressLine1?.trim() ? values.addressLine1.trim() : null,
      timezone: values.timezone,
      salesNotApplicable: values.salesNotApplicable,
      serviceNotApplicable: values.serviceNotApplicable,
      partsNotApplicable: values.partsNotApplicable,
    });
}

export function DealershipRooftopsPage() {
  const { dealershipId } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Rooftop | "new" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Rooftop | null>(null);

  const query = useQuery({
    queryKey: ["admin-bdc", dealershipId, "rooftops"],
    queryFn: () => adminApi.listRooftops(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  const deleteMutation = useMutation({
    mutationFn: (rooftopId: string) => adminApi.removeRooftop(dealershipId!, rooftopId),
    onSuccess: async () => {
      setPendingDelete(null);
      await invalidate(qc, dealershipId);
      toast.success("Rooftop deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const rooftops = query.data ?? [];

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Locations"
        title="Rooftops"
        description="Physical locations operating under this dealership. Each rooftop scopes inventory, channels, and assignments."
        actions={
          <Button onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            New rooftop
          </Button>
        }
      />
      <Section
        title={
          query.data
            ? `${rooftops.length} ${rooftops.length === 1 ? "rooftop" : "rooftops"}`
            : "Rooftops"
        }
      >
        {query.isLoading ? (
          <PageLoading message="Loading rooftops…" />
        ) : query.isError ? (
          <PageError
            title="Couldn't load rooftops"
            description="Try refreshing the page."
          />
        ) : rooftops.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-5 w-5" />}
            title="No rooftops yet"
            description="Add the first rooftop so contacts, vehicles, and channels can be attached."
            action={
              <Button onClick={() => setEditing("new")}>
                <Plus className="h-4 w-4" />
                Add rooftop
              </Button>
            }
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Primary contact</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rooftops.map((rooftop) => (
                    <TableRow key={rooftop.id}>
                      <TableCell className="font-medium">{rooftop.name}</TableCell>
                      <TableCell>
                        <Badge variant={rooftop.status === "active" ? "success" : "secondary"}>
                          {rooftop.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {[rooftop.city, rooftop.state, rooftop.zip].filter(Boolean).join(", ") || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {rooftop.primaryEmail ?? rooftop.primaryPhone ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit rooftop"
                          onClick={() => setEditing(rooftop)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete rooftop"
                          onClick={() => setPendingDelete(rooftop)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            {/* Mobile: condensed touch list with the same data hierarchy. */}
            <MobileList>
              {rooftops.map((rooftop) => (
                <MobileListRow
                  key={rooftop.id}
                  primary={rooftop.name}
                  secondary={rooftop.primaryEmail ?? rooftop.primaryPhone ?? "—"}
                  tertiary={
                    [rooftop.city, rooftop.state, rooftop.zip].filter(Boolean).join(", ") || "—"
                  }
                  meta={
                    <Badge variant={rooftop.status === "active" ? "success" : "secondary"}>
                      {rooftop.status}
                    </Badge>
                  }
                  trailing={
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit rooftop"
                        onClick={() => setEditing(rooftop)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete rooftop"
                        onClick={() => setPendingDelete(rooftop)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      {editing !== null && dealershipId && (
        <RooftopFormDialog
          mode={editing === "new" ? "create" : "edit"}
          rooftop={editing === "new" ? null : editing}
          dealershipId={dealershipId}
          onClose={() => setEditing(null)}
        />
      )}

      <Dialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete rooftop</DialogTitle>
            <DialogDescription>
              {pendingDelete &&
                `“${pendingDelete.name}” will be removed. Contacts and inventory pinned to this rooftop will be unscoped (set to dealership-wide).`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete rooftop"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RooftopFormDialog({
  mode,
  rooftop,
  dealershipId,
  onClose,
}: {
  mode: "create" | "edit";
  rooftop: Rooftop | null;
  dealershipId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const dealershipQ = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId),
  });
  const country = dealershipQ.data?.country ?? "US";
  const isCa = country === "CA";
  const formSchema = useMemo(() => rooftopFormSchemaForCountry(country), [country]);
  const form = useZodForm(
    formSchema,
    rooftop ? toEditValues(rooftop) : EMPTY,
  );

  const createMutation = useMutation({
    mutationFn: (input: RooftopFormValues) =>
      adminApi.createRooftop(
        dealershipId,
        toPayload(input) as Parameters<typeof adminApi.createRooftop>[1],
      ),
    onSuccess: async () => {
      await invalidate(qc, dealershipId);
      toast.success("Rooftop created");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: RooftopFormValues) =>
      adminApi.updateRooftop(dealershipId, rooftop!.id, toPayload(input)),
    onSuccess: async () => {
      await invalidate(qc, dealershipId);
      toast.success("Rooftop updated");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") {
      createMutation.mutate(result.data);
    } else {
      updateMutation.mutate(result.data);
    }
  }

  const { values, errors, setField } = form;
  // Once the admin has typed anything, block the implicit close paths
  // (outside click, Escape) so a stray click can't wipe the form.
  const isDirty =
    JSON.stringify(values) !== JSON.stringify(rooftop ? toEditValues(rooftop) : EMPTY);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New rooftop" : "Edit rooftop"}</DialogTitle>
          <DialogDescription>
            Identity, address, and the day-to-day operations contact.
          </DialogDescription>
        </DialogHeader>
        {/*
          The grid scrolls when the form is taller than the dialog.
          Negative-margin + padding so focus rings + input shadows
          have breathing room inside the clip box (Tailwind's
          `ring-2 ring-offset-1` paints 3px outside the element;
          without this, the ring + shadow get clipped at the
          scroll-area edges).
        */}
        <div className="grid gap-5 overflow-y-auto -mx-2 -my-1.5 px-2 py-1.5">
          <FieldGroup title="Identity">
            <FormField label="Name" required error={errors.name}>
              <Input
                value={values.name}
                onChange={(e) => setField("name", e.target.value)}
                autoFocus
                placeholder="Sunshine Auto — Downtown"
              />
            </FormField>
            <FormField label="Status" required error={errors.status}>
              <Select
                value={values.status}
                onValueChange={(v) => setField("status", v as RooftopFormValues["status"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RooftopStatusSchema.options.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Website" optional error={errors.websiteUrl}>
              <Input
                type="url"
                value={values.websiteUrl ?? ""}
                onChange={(e) => setField("websiteUrl", e.target.value)}
                placeholder="https://sunshineauto.com"
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Location">
            <FormField className="sm:col-span-2" label="Street" optional error={errors.addressLine1}>
              <Input
                value={values.addressLine1 ?? ""}
                onChange={(e) => setField("addressLine1", e.target.value)}
                placeholder="123 Main St"
              />
            </FormField>
            <FormField label="City" optional error={errors.city}>
              <Input
                value={values.city ?? ""}
                onChange={(e) => setField("city", e.target.value)}
              />
            </FormField>
            <FormField label={isCa ? "Province" : "State"} optional error={errors.state}>
              <Select
                value={values.state === "" ? "__none__" : (values.state ?? "__none__")}
                onValueChange={(v) =>
                  setField("state", v === "__none__" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={isCa ? "Pick a province" : "Pick a state"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {/* Imported rooftops can carry the other country's code —
                      keep it selectable so the trigger isn't blank. */}
                  {values.state &&
                    !stateOptionsForCountry(country).some((s) => s.code === values.state) && (
                      <SelectItem value={values.state}>{values.state}</SelectItem>
                    )}
                  {stateOptionsForCountry(country).map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.code} — {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label={isCa ? "Postal code" : "ZIP"} optional error={errors.zip}>
              <Input
                value={values.zip ?? ""}
                onChange={(e) => setField("zip", e.target.value)}
                placeholder={isCa ? "A1A 1A1" : "12345 or 12345-6789"}
                maxLength={isCa ? 7 : 10}
              />
            </FormField>
            <FormField label="Timezone" required error={errors.timezone}>
              <Select
                value={values.timezone}
                onValueChange={(v) => setField("timezone", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Imported rooftops can carry any IANA zone — keep the
                      current value selectable so the trigger isn't blank
                      and an unrelated edit can't silently overwrite it. */}
                  {values.timezone && !timezonesForCountry(country).includes(values.timezone) && (
                    <SelectItem value={values.timezone}>{values.timezone}</SelectItem>
                  )}
                  {timezonesForCountry(country).map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FieldGroup>

          <FieldGroup title="Public contact">
            <FormField
              label="Rooftop phone"
              optional
              error={errors.primaryPhone}
              description="E.164 format, e.g. +15551234567."
            >
              <Input
                type="tel"
                inputMode="tel"
                value={values.primaryPhone ?? ""}
                onChange={(e) => setField("primaryPhone", e.target.value)}
                placeholder="+15551234567"
              />
            </FormField>
            <FormField label="Rooftop email" optional error={errors.primaryEmail}>
              <Input
                type="email"
                value={values.primaryEmail ?? ""}
                onChange={(e) => setField("primaryEmail", e.target.value)}
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Departments">
            <p className="-mt-1 text-xs text-muted-foreground sm:col-span-2">
              Switch off any department this location doesn&rsquo;t run — the AI
              is told not to offer or book a department that&rsquo;s turned off.
            </p>
            <div className="divide-y divide-border overflow-hidden rounded-lg border bg-card sm:col-span-2">
              <DepartmentToggle
                icon={Car}
                title="Sales"
                description="Vehicle sales, test drives, and showroom visits."
                available={!values.salesNotApplicable}
                onAvailableChange={(available) =>
                  setField("salesNotApplicable", !available)
                }
              />
              <DepartmentToggle
                icon={Wrench}
                title="Service"
                description="Maintenance and repair appointments at the service drive."
                available={!values.serviceNotApplicable}
                onAvailableChange={(available) =>
                  setField("serviceNotApplicable", !available)
                }
              />
              <DepartmentToggle
                icon={Package}
                title="Parts"
                description="Parts and accessory orders or counter pickups."
                available={!values.partsNotApplicable}
                onAvailableChange={(available) =>
                  setField("partsNotApplicable", !available)
                }
              />
            </div>
          </FieldGroup>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending ? "Saving…" : mode === "create" ? "Create rooftop" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

/**
 * A single department on/off row: icon tile + name + description on the
 * left, switch on the right. Presented as the positive capability ("on =
 * offered here") even though the persisted flag is inverse
 * (`*NotApplicable`) — the parent inverts at the boundary, so the default
 * (all departments present) reads as every switch on.
 */
function DepartmentToggle({
  icon: Icon,
  title,
  description,
  available,
  onAvailableChange,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  available: boolean;
  onAvailableChange: (available: boolean) => void;
}) {
  const id = useId();
  const descriptionId = `${id}-description`;
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div
        className={cn(
          "flex items-start gap-3 transition-opacity",
          !available && "opacity-55",
        )}
      >
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/40 text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
        <div className="grid gap-1">
          <label
            htmlFor={id}
            className="flex cursor-pointer items-center gap-2 text-sm font-medium leading-none"
          >
            {title}
            {!available && (
              <span className="rounded-full border bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Not offered
              </span>
            )}
          </label>
          <p id={descriptionId} className="text-xs text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
      <Switch
        id={id}
        checked={available}
        onCheckedChange={onAvailableChange}
        aria-describedby={descriptionId}
      />
    </div>
  );
}

async function invalidate(qc: ReturnType<typeof useQueryClient>, dealershipId?: string) {
  if (!dealershipId) return;
  await Promise.all([
    qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "rooftops"] }),
    qc.invalidateQueries({ queryKey: ["dealership", dealershipId] }),
  ]);
}
